import { Plus, Trash2, CreditCard, CheckCircle2, AlertTriangle } from "lucide-react";
import { idr } from "../../lib/api";
import InputRupiah from "../InputRupiah";
import { PaymentMethodSelect, adalahKreditRetur } from "./MetodePembayaran";
import { barisBaru, ringkasBlokPembayaran } from "../../lib/payloadNota";

/* ============================================================================
 * Blok pembayaran kasir (batch N3, brief owner 22 Sep 2026 D4–D7) — dipakai
 * Input Nota (draf) dan Input Penjualan (dokumen bertanda).
 *
 * Susunannya mengikuti brief: baris metode pembayaran UTAMA, lalu kotak
 * centang "Tambah pembayaran lain" yang memunculkan baris dengan TIGA kolom
 * wajib (keterangan bebas, metode, nominal — contoh halaman 16: "Akulaku |
 * 5.000.000"), lalu trade-in & DP (`children`, D5/D7: "ditempatkan di bawah
 * metode pembayaran"), lalu ringkasan uang.
 *
 * Semua angka ringkasan datang dari `lib/payloadNota.js::ringkasBlokPembayaran`
 * (cermin aturan server) — komponen ini tidak menghitung uang sendiri.
 * ========================================================================== */
export default function BlokPembayaran({
  rows, onChange, groups, rincian, tempo = false, testidPrefix = "nota-bayar",
  sembunyikanKreditRetur = false, children,
}) {
  const baris = rows && rows.length ? rows : [barisBaru("Cash", 0, !tempo)];
  const k = ringkasBlokPembayaran(rincian, baris, { tempo });
  const p = testidPrefix;
  const adaTambahan = baris.length > 1;
  // Kredit Retur disembunyikan hanya bila jalurnya menolak (edit Input
  // Penjualan: kredit hanya dipotong saat nota DIBUAT — server 409).
  const sembunyikan = sembunyikanKreditRetur ? adalahKreditRetur : undefined;

  const ubah = (i, patch) => {
    const arr = baris.map((b, j) => (j === i ? { ...b, ...patch } : b));
    onChange(arr);
  };
  const tambah = () => onChange([...baris, barisBaru("", 0, false)]);
  const hapus = (i) => onChange(baris.filter((_, j) => j !== i));
  const isiSisa = () => {
    // Samakan baris utama dengan sisa yang harus dibayar di kasir (tunai).
    const lain = baris.slice(1).reduce((a, b) => a + (Number(b.amount) || 0), 0);
    ubah(0, { amount: Math.max(0, k.harusDiKasir - lain), _ikutSisa: true });
  };

  return (
    <div className="border border-slate-200 rounded-lg p-3 bg-slate-50/50 space-y-2" data-testid={`${p}-blok`}>
      <p className="text-xs font-semibold uppercase tracking-wider text-slate-600 flex items-center gap-1.5">
        <CreditCard className="w-3.5 h-3.5" /> Metode Pembayaran
      </p>

      {/* Baris UTAMA — tanpa kolom keterangan (contoh halaman 16). Nota
          bertempo: uang muka OPSIONAL, bawaan 0 — bukan mengikuti tagihan. */}
      <div className="grid grid-cols-[1fr,150px] sm:grid-cols-[1fr,180px] gap-2 items-center">
        <PaymentMethodSelect value={baris[0].method} groups={groups}
          onChange={v => ubah(0, { method: v, metode_id: null })}
          sembunyikan={sembunyikan}
          testid={`${p}-0-metode`} />
        <InputRupiah value={baris[0].amount || ""} testid={`${p}-0-nominal`}
          onChange={v => ubah(0, { amount: v || 0, _ikutSisa: false })}
          placeholder={tempo ? "Uang muka (opsional)" : "Nominal"}
          className="h-9 px-3 rounded border border-slate-200 text-sm text-right font-mono bg-white" />
      </div>
      {tempo ? (
        <p className="text-[11px] text-amber-700" data-testid={`${p}-0-petunjuk-tempo`}>
          Nota bertempo: isi nominal hanya kalau pelanggan membayar UANG MUKA sekarang.
          Kosong = belum dibayar — seluruh sisa jadi piutang.
        </p>
      ) : (!baris[0]._ikutSisa && Math.abs(k.selisih) > 0.5 && !k.downgrade) ? (
        <button type="button" onClick={isiSisa} data-testid={`${p}-0-isi-sisa`}
          className="text-[11px] font-medium text-emerald-700 hover:text-emerald-800 underline">
          Isi sisa {idr(Math.max(0, k.harusDiKasir - baris.slice(1).reduce((a, b) => a + (Number(b.amount) || 0), 0)))}
        </button>
      ) : null}

      <label className="inline-flex items-center gap-2 text-xs cursor-pointer select-none">
        <input type="checkbox" checked={adaTambahan} data-testid={`${p}-tambah`}
          onChange={e => (e.target.checked ? tambah() : onChange([baris[0]]))} />
        Tambah pembayaran lain
      </label>

      {adaTambahan && (
        <div className="space-y-2">
          {baris.slice(1).map((b, j) => {
            const i = j + 1;
            const kurangKet = (Number(b.amount) || 0) > 0 && !(b.keterangan || "").trim();
            return (
              <div key={i} className="grid grid-cols-1 sm:grid-cols-[1fr,1fr,150px,auto] gap-2 items-center">
                <input value={b.keterangan || ""} data-testid={`${p}-${i}-ket`}
                  onChange={e => ubah(i, { keterangan: e.target.value })}
                  maxLength={120}
                  placeholder="Keterangan * (mis. leasing / no. kontrak / tenor)"
                  className={`h-9 px-3 rounded border text-sm bg-white ${kurangKet ? "border-rose-400" : "border-slate-200"}`} />
                <PaymentMethodSelect value={b.method} groups={groups}
                  onChange={v => ubah(i, { method: v, metode_id: null })}
                  sembunyikan={sembunyikan}
                  testid={`${p}-${i}-metode`} />
                <InputRupiah value={b.amount || ""} testid={`${p}-${i}-nominal`}
                  onChange={v => ubah(i, { amount: v || 0 })}
                  className="h-9 px-3 rounded border border-slate-200 text-sm text-right font-mono bg-white" />
                <button type="button" onClick={() => hapus(i)} data-testid={`${p}-${i}-hapus`}
                  className="p-1.5 hover:bg-rose-50 rounded text-slate-400 hover:text-rose-600 justify-self-end">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            );
          })}
          <button type="button" onClick={tambah} data-testid={`${p}-tambah-baris`}
            className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 hover:text-emerald-800">
            <Plus className="w-3.5 h-3.5" /> Tambah baris pembayaran
          </button>
        </div>
      )}

      {/* D5 & D7: trade-in dan DP DI BAWAH metode pembayaran. */}
      {children}

      <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs space-y-0.5"
        data-testid={`${p}-ringkasan`}>
        <div className="flex justify-between text-slate-600">
          <span>Nilai tagihan</span><span className="font-mono">{idr(k.nilaiTagihan)}</span>
        </div>
        {k.tradeIn > 0 && (
          <div className="flex justify-between text-violet-700" data-testid={`${p}-ringkasan-tradein`}>
            <span>− Trade-in</span><span className="font-mono">{idr(k.tradeIn)}</span>
          </div>
        )}
        {k.dp > 0 && (
          <div className="flex justify-between text-emerald-700" data-testid={`${p}-ringkasan-dp`}>
            <span>− DP</span><span className="font-mono">{idr(k.dp)}</span>
          </div>
        )}
        {k.downgrade ? (
          <div className="flex justify-between font-semibold text-amber-800 border-t border-slate-100 pt-0.5"
            data-testid={`${p}-ringkasan-kembali`}>
            <span>Dikembalikan ke pelanggan (ditransfer Finance)</span>
            <span className="font-mono">{idr(k.kembaliKePelanggan)}</span>
          </div>
        ) : (
          <div className="flex justify-between font-semibold text-slate-900 border-t border-slate-100 pt-0.5">
            <span>{tempo ? "Tagihan setelah trade-in & DP" : "Harus dibayar di kasir"}</span>
            <span className="font-mono" data-testid={`${p}-ringkasan-harus`}>{idr(k.harusDiKasir)}</span>
          </div>
        )}
        <div className="flex justify-between text-slate-600">
          <span>Pembayaran</span><span className="font-mono">{idr(k.jumlah)}</span>
        </div>
        {tempo ? (
          <p className="text-amber-800" data-testid={`${p}-status`}>
            Sisa {idr(k.sisaPiutang)} menjadi piutang (ditagih Finance).
          </p>
        ) : k.downgrade ? (
          <p className="text-amber-800" data-testid={`${p}-status`}>
            Pelanggan tidak membayar di kasir — isi rekening tujuan pengembalian.
          </p>
        ) : Math.abs(k.selisih) <= 1 ? (
          <p className="text-emerald-700 inline-flex items-center gap-1" data-testid={`${p}-status`}>
            <CheckCircle2 className="w-3.5 h-3.5" /> Pembayaran sesuai tagihan
          </p>
        ) : (
          <p className="text-rose-700 inline-flex items-center gap-1" data-testid={`${p}-status`}>
            <AlertTriangle className="w-3.5 h-3.5" /> Selisih {idr(k.selisih)} — betulkan nominal pembayaran
          </p>
        )}
      </div>
    </div>
  );
}
